/**
 * Shared pieces for the special-sources citation generators.
 *
 * The general helpers live in `citation-parts.ts` (names, author lists, dates) and in
 * the legal-sources family's `common.ts` (field reads, italic fragments, title case,
 * "Month D, YYYY" dates), and the generators import them from there. What is here is
 * what those two do not cover: the National Library of Medicine's name and date forms
 * for Vancouver references, a list of people typed one per line, APA's in-text author
 * forms with a year, and the small text checks several generators need.
 */

import {
  apaInTextAuthors,
  apaNameInitialsFirst,
  type CitationParts,
  naturalName,
  type PartialDate,
  type PersonName,
  parsePersonName,
} from "@/lib/tool-kit/citation-parts";

/** Field values as the builder stores them. */
export type SpecialValues = Readonly<Record<string, string | undefined>>;

/** A citation part with an optional bold flag — figure numbers are bold in APA. */
export interface RichPart {
  text: string;
  italic?: boolean;
  bold?: boolean;
}

const RE_LINE_BREAK = /\r?\n/;

/**
 * People typed one per line — "Mary Jane Smith", "Smith, Mary Jane" — in order. Blank
 * lines are ignored.
 */
export function parsePeople(text: string): PersonName[] {
  const people: PersonName[] = [];
  for (const line of text.split(RE_LINE_BREAK)) {
    const person = parsePersonName(line);
    if (person) {
      people.push(person);
    }
  }
  return people;
}

/** One organization, or nothing when the field is blank. */
export function organizationOrNothing(name: string): PersonName[] {
  const trimmed = name.trim();
  return trimmed === ""
    ? []
    : [{ given: [], family: trimmed, organization: true }];
}

/*
 * NLM (Citing Medicine, 2nd ed.): surname, then the initials of up to two given names
 * with no periods or spaces — "Cannon R", "Patrias K". A hyphenated given name gives
 * both initials without the hyphen ("Jean-Louis" → "JL"). Organizations as written.
 */
const RE_NAME_PIECES = /[\s-]+/;

export function nlmName(name: PersonName): string {
  if (name.organization || name.given.length === 0) {
    return name.suffix ? `${name.family} ${name.suffix}` : name.family;
  }
  const initials = name.given
    .flatMap((given) => given.split(RE_NAME_PIECES))
    .filter(Boolean)
    .slice(0, 2)
    .map((piece) => piece.charAt(0).toUpperCase())
    .join("");
  const base = `${name.family} ${initials}`;
  // Citing Medicine writes "Jr" and "III" after the initials, without punctuation.
  return name.suffix ? `${base} ${name.suffix.replace(".", "")}` : base;
}

/** Citing Medicine's dates: year, three-letter month, day — "2020 May 11". */
const NLM_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function nlmDate(date: PartialDate | null): string {
  if (!date) {
    return "";
  }
  if (!date.month) {
    return String(date.year);
  }
  const month = NLM_MONTHS[date.month - 1];
  return date.day
    ? `${date.year} ${month} ${date.day}`
    : `${date.year} ${month}`;
}

/** APA in-text: "(Smith & Lee, 2020)" or, narratively, "Smith and Lee (2020)". */
export function apaInText(
  names: PersonName[],
  year: string,
  narrative: boolean
): string {
  const who = apaInTextAuthors(names, narrative);
  if (narrative) {
    return `${who} (${year})`;
  }
  return `(${who}, ${year})`;
}

/** A plain-text rendering of rich parts. */
export function richToPlain(parts: readonly RichPart[]): string {
  return parts.map((part) => part.text).join("");
}

/** Drop the bold flag — what the shared builder and its tests compare. */
export function richToParts(parts: readonly RichPart[]): CitationParts {
  return parts.map((part) =>
    part.italic ? { text: part.text, italic: true } : { text: part.text }
  );
}

const RE_URL = /^https?:\/\/\S+$/i;

/** True for an http(s) address with no spaces. */
export function looksLikeUrl(text: string): boolean {
  return RE_URL.test(text.trim());
}

const RE_DOI_PREFIX = /^(?:https?:\/\/(?:dx\.)?doi\.org\/|doi:\s*)/i;
const RE_BARE_DOI = /^10\.\d{4,9}\/\S+$/;

/**
 * "10.5281/zenodo.123", "doi:10.5281/zenodo.123" or a doi.org link → the
 * "https://doi.org/…" form APA 7 and the other styles print. Null if it is not a DOI.
 */
export function doiUrl(text: string): string | null {
  const bare = text.trim().replace(RE_DOI_PREFIX, "");
  return RE_BARE_DOI.test(bare) ? `https://doi.org/${bare}` : null;
}

/* ------------------------------------------------------------- book parts */

/*
 * Editors and translators, the way each style names them after a book's title.
 * APA 7 (§9.39–9.41; "Book/ebook references", "Republished book, with editor"):
 * "(E. Wilson, Trans.)", "(D. Webb, Ed.)", "(J. Strachey, Ed. & Trans.)", joined with a
 * semicolon when they are different people. MLA 9 (§5.12–5.18, contributor element):
 * "Translated by Emily Wilson", "Edited by …". Chicago 18 (§14.6): "trans. Emily
 * Wilson" in a note, "Translated by Emily Wilson." in a bibliography.
 */
export interface Contributors {
  editors: PersonName[];
  translators: PersonName[];
}

function sameNames(a: PersonName[], b: PersonName[]): boolean {
  return (
    a.length > 0 &&
    a.length === b.length &&
    a.every(
      (name, index) =>
        name.family === b[index].family &&
        name.given.join(" ") === b[index].given.join(" ")
    )
  );
}

/** "A", "A & B", "A, B, & C" — APA's list of non-author contributors. */
function apaRoleList(names: PersonName[]): string {
  const formatted = names.map(apaNameInitialsFirst);
  if (formatted.length <= 1) {
    return formatted.join("");
  }
  if (formatted.length === 2) {
    return `${formatted[0]} & ${formatted[1]}`;
  }
  return `${formatted.slice(0, -1).join(", ")}, & ${formatted.at(-1)}`;
}

/** The parenthetical after an APA title, without the parentheses: "E. Wilson, Trans." */
export function apaContributors({
  editors,
  translators,
}: Contributors): string {
  if (sameNames(editors, translators)) {
    return `${apaRoleList(editors)}, ${editors.length > 1 ? "Eds." : "Ed."} & Trans.`;
  }
  const pieces: string[] = [];
  if (editors.length > 0) {
    pieces.push(
      `${apaRoleList(editors)}, ${editors.length > 1 ? "Eds." : "Ed."}`
    );
  }
  if (translators.length > 0) {
    pieces.push(`${apaRoleList(translators)}, Trans.`);
  }
  return pieces.join("; ");
}

/** "A", "A and B", "A et al." — MLA 9 lists other contributors like authors. */
function mlaRoleList(names: PersonName[]): string {
  if (names.length >= 3) {
    return `${naturalName(names[0])} et al.`;
  }
  return names.map(naturalName).join(" and ");
}

/** "Translated by Emily Wilson", "Edited and translated by …", "Translated by …, edited by …". */
export function mlaContributors({
  editors,
  translators,
}: Contributors): string {
  if (sameNames(editors, translators)) {
    return `Edited and translated by ${mlaRoleList(editors)}`;
  }
  const pieces: string[] = [];
  if (translators.length > 0) {
    pieces.push(`Translated by ${mlaRoleList(translators)}`);
  }
  if (editors.length > 0) {
    pieces.push(
      `${pieces.length > 0 ? "edited" : "Edited"} by ${mlaRoleList(editors)}`
    );
  }
  return pieces.join(", ");
}

/** "A", "A and B", "A, B, and C"; seven or more → first three, et al. (Chicago 18). */
function chicagoRoleList(names: PersonName[], note: boolean): string {
  const natural = names.map(naturalName);
  if (note && natural.length >= 3) {
    return `${natural[0]} et al.`;
  }
  if (!note && natural.length > 6) {
    return `${natural.slice(0, 3).join(", ")}, et al.`;
  }
  if (natural.length <= 2) {
    return natural.join(" and ");
  }
  return `${natural.slice(0, -1).join(", ")}, and ${natural.at(-1)}`;
}

/** Chicago note: "trans. Emily Wilson", "ed. and trans. …", "ed. …, trans. …". */
export function chicagoNoteContributors({
  editors,
  translators,
}: Contributors): string {
  if (sameNames(editors, translators)) {
    return `ed. and trans. ${chicagoRoleList(editors, true)}`;
  }
  const pieces: string[] = [];
  if (editors.length > 0) {
    pieces.push(`ed. ${chicagoRoleList(editors, true)}`);
  }
  if (translators.length > 0) {
    pieces.push(`trans. ${chicagoRoleList(translators, true)}`);
  }
  return pieces.join(", ");
}

/** Chicago bibliography: "Translated by Emily Wilson." "Edited and translated by …." */
export function chicagoBibliographyContributors({
  editors,
  translators,
}: Contributors): string {
  if (sameNames(editors, translators)) {
    return `Edited and translated by ${chicagoRoleList(editors, false)}.`;
  }
  const pieces: string[] = [];
  if (editors.length > 0) {
    pieces.push(`Edited by ${chicagoRoleList(editors, false)}.`);
  }
  if (translators.length > 0) {
    pieces.push(`Translated by ${chicagoRoleList(translators, false)}.`);
  }
  return pieces.join(" ");
}

/*
 * Publisher names. APA 7 (§9.29) drops designations of business structure — Inc.,
 * Ltd., LLC, Co. Chicago 18 (§14.33) and MLA 9 (as the Purdue OWL summarizes its
 * appendix 1) also drop "& Co.", "& Company" and a closing "Company"; MLA drops an
 * initial "The" and shortens university presses to "UP" and "U … P" — "Harvard UP",
 * "U of Chicago P". A word that is the name itself stays: "Psychological
 * Corporation" is not shortened to "Psychological".
 */
const RE_BUSINESS_STRUCTURE =
  /,?\s+(?:Inc\.?|Ltd\.?|LLC|L\.L\.C\.|Co\.|Corp\.)$/i;
const RE_AND_COMPANY = /,?\s+(?:&|and)\s+(?:Co\.?|Company)$/i;
const RE_TRAILING_COMPANY = /^(\S+\s+\S+.*?)\s+Company$/;
const RE_LEADING_THE = /^The\s+/;
const RE_UNIVERSITY_PRESS = /\bUniversity Press\b/g;
const RE_UNIVERSITY_OF_PRESS = /^University of (.+) Press$/;
const RE_UNIVERSITY = /\bUniversity\b/g;

export type PublisherStyle = "apa" | "mla" | "chicago";

export function publisherName(name: string, style: PublisherStyle): string {
  let out = name.trim().replace(RE_BUSINESS_STRUCTURE, "");
  if (style === "apa") {
    return out;
  }
  out = out.replace(RE_AND_COMPANY, "").replace(RE_TRAILING_COMPANY, "$1");
  if (style === "chicago") {
    return out;
  }
  out = out.replace(RE_LEADING_THE, "");
  const universityOf = RE_UNIVERSITY_OF_PRESS.exec(out);
  if (universityOf) {
    return `U of ${universityOf[1]} P`;
  }
  return out.replace(RE_UNIVERSITY_PRESS, "UP").replace(RE_UNIVERSITY, "U");
}

/* ------------------------------------------------------------- page ranges */

const RE_PAGE_RANGE = /^\s*([A-Za-z]?\d+)\s*(?:[-–—]|to)\s*([A-Za-z]?\d+)\s*$/;
const RE_ONLY_DIGITS = /^\d+$/;

/** "606-613", "606–613" or "606 to 613" → ["606", "613"]; a single page → [page, ""]. */
export function splitPages(text: string): [string, string] {
  const match = RE_PAGE_RANGE.exec(text);
  if (match) {
    return [match[1], match[2]];
  }
  return [text.trim(), ""];
}

/**
 * Chicago 18 inclusive numbers (§9.63): under 100, or a first number that is a
 * multiple of 100, give every digit (71–72, 100–104); a first number 101–109 past a
 * hundred gives only the changed part (101–8, 1103–4); 110–199 past a hundred gives at
 * least two digits (321–28, 1496–500); a four-digit range where three digits change
 * gives all four (1496–1504).
 */
export function chicagoPageRange(first: string, last: string): string {
  const from = first.trim();
  const to = last.trim();
  if (to === "" || to === from) {
    return from;
  }
  if (
    !(RE_ONLY_DIGITS.test(from) && RE_ONLY_DIGITS.test(to)) ||
    Number(to) < Number(from)
  ) {
    return `${from}–${to}`;
  }
  const start = Number(from);
  const remainder = start % 100;
  if (start < 100 || remainder === 0 || to.length !== from.length) {
    return `${from}–${to}`;
  }
  let shared = 0;
  while (shared < from.length - 1 && from[shared] === to[shared]) {
    shared += 1;
  }
  let changed = to.slice(shared);
  if (remainder >= 10 && changed.length < 2) {
    changed = to.slice(-2);
  }
  if (from.length === 4 && changed.length >= 3) {
    return `${from}–${to}`;
  }
  return `${from}–${changed}`;
}
